import validator from "validator";
import { getFunctions, httpsCallable } from "firebase/functions";
// eslint-disable-next-line no-unused-vars
import firebase from "../firebase"; // this is needed to make the test pass.
import { hasResourceType, resourceTypeIncludes } from "./normalizeResourceType";
import { eovs } from "../eovs";

export const validateEmail = (email) => !email || validator.isEmail(email);
export const validateURL = (url) => !url || validator.isURL(url);
const functions = getFunctions();
const checkURLActive = httpsCallable(functions, "checkURLActive");

// See https://stackoverflow.com/a/48524047/7416701
export const doiRegexp =
  /^(https:\/\/doi.org\/)?10\.\d{4,9}\/[-._;()/:A-Z0-9]+$/i;
function isValidHttpUrl(string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}
export const validateDOI = (val) =>
  !val || (doiRegexp.test(val) && isValidHttpUrl(val));
const validateLatitude = (num) => num >= -90 && num <= 90;

const deepCompare = (obj1, obj2) =>
  JSON.parse(JSON.stringify(obj1) === JSON.stringify(obj2));

const validateLongitude = (num) => num >= -360 && num <= 360;

const polygonIsValid = (polygon) => {
  // eg 48,-128 56,-133 56,-147 48,-128
  const coordinates = polygon.split(" ").map((c) => c.split(","));
  if (coordinates.length < 2) return false;
  if (!deepCompare(coordinates[0], coordinates[coordinates.length - 1]))
    return false;

  return (
    coordinates.filter(
      ([lat, lon]) =>
        validateLongitude(parseFloat(lon)) && validateLatitude(parseFloat(lat)),
    ).length === coordinates.length
  );
};

const bboxLabels = {
  north: { en: "North", fr: "Nord" },
  south: { en: "South", fr: "Sud" },
  east: { en: "East", fr: "Est" },
  west: { en: "West", fr: "Ouest" },
};

// Specific complaints about a bounding box, so the user gets more than "Spatial information is missing".
// Returns [] when nothing is filled in (that's the generic "missing" case) or when the box is fine.
export const bboxProblems = (map = {}) => {
  const coords = {
    north: parseFloat(map.north),
    south: parseFloat(map.south),
    east: parseFloat(map.east),
    west: parseFloat(map.west),
  };
  const names = (keys, language) =>
    keys.map((k) => bboxLabels[k][language]).join(", ");
  const problems = [];

  const missing = Object.keys(coords).filter((k) => Number.isNaN(coords[k]));
  if (missing.length && missing.length < 4)
    problems.push({
      en: `Missing bounding box coordinate(s): ${names(missing, "en")}`,
      fr: `Coordonnée(s) de délimitation manquante(s) : ${names(missing, "fr")}`,
    });

  const outOfRange = Object.keys(coords).filter(
    (k) =>
      !Number.isNaN(coords[k]) &&
      !(k === "north" || k === "south"
        ? validateLatitude(coords[k])
        : validateLongitude(coords[k])),
  );
  if (outOfRange.length)
    problems.push({
      en: `Coordinate(s) out of range: ${names(
        outOfRange,
        "en",
      )}. Latitudes must be between -90 and 90, longitudes between -360 and 360`,
      fr: `Coordonnée(s) hors limites : ${names(
        outOfRange,
        "fr",
      )}. Les latitudes doivent être comprises entre -90 et 90, les longitudes entre -360 et 360`,
    });

  // NaN comparisons are false, so these only fire once both values are numbers
  if (coords.north < coords.south)
    problems.push({
      en: "North latitude must be greater than or equal to South latitude",
      fr: "La latitude Nord doit être supérieure ou égale à la latitude Sud",
    });

  if (coords.east < coords.west)
    problems.push({
      en: "East longitude must be greater than or equal to West longitude",
      fr: "La longitude Est doit être supérieure ou égale à la longitude Ouest",
    });

  return problems;
};

const contactIsFilled = (contact) =>
  Boolean(
    contact.role &&
    contact.role.length &&
    (contact.orgName || contact.givenNames || contact.lastName),
  );

// required fields and  a function to validate each
const validators = {
  title: {
    validation: (val) => val && val.en && val.fr,
    tab: "start",
    error: {
      en: "Missing title in French or English",
      fr: "Titre manquant en français ou en anglais",
    },
  },
  resourceType: {
    validation: (val) => hasResourceType(val),
    tab: "start",
    error: {
      en: "Please select a theme for this record",
      fr: "Veuillez sélectionner une discipline scientifique pour cet enregistrement",
    },
  },
  abstract: {
    validation: (val) => val && val.en && val.fr,
    tab: "dataID",
    error: {
      en: "Missing abstract in French or English",
      fr: "Résumé manquant en français ou en anglais",
    },
  },
  keywords: {
    validation: (val) => val && (val.en.length || val.fr.length),
    tab: "dataID",
    error: {
      en: "At least one keyword is required",
      fr: "Au moins un mot clé est requis",
    },
  },
  eov: {
    validation: (val) => val && val.length,
    tab: "dataID",
    error: {
      en: "At least one EOV is required",
      fr: "Au moins une variable océanique essentielle est requise",
    },
  },
  eovDeprecated: {
    validation: (_, record) => {
      const deprecatedValues = new Set(
        eovs.filter((e) => e.deprecated).map((e) => e.value),
      );
      const eov = Array.isArray(record.eov) ? record.eov : [];
      return !eov.some((v) => deprecatedValues.has(v));
    },
    tab: "dataID",
    error: {
      en: "One or more selected EOVs are deprecated and prevent submission. Please unselect them and choose the replacement EOVs.",
      fr: "Une ou plusieurs EOVs sélectionnées sont dépréciées et empêchent la soumission. Veuillez les désélectionner et choisir les EOVs de remplacement.",
    },
  },
  datasetIdentifier: {
    validation: validateDOI,
    optional: true,
    tab: "start",
    error: {
      en: "Invalid DOI",
      fr: "DOI non valide",
    },
  },
  metadataScope: {
    tab: "start",
    validation: (val) => val,
    error: {
      en: "Please select a resource type",
      fr: "Veuillez sélectionner un type de ressources",
    },
  },
  progress: {
    tab: "dataID",
    validation: (val) => val,
    error: {
      en: "Please select a dataset status",
      fr: "Veuillez définir le statut du jeu de données",
    },
  },
  language: {
    tab: "dataID",
    validation: (val) => val,
    error: {
      en: "Language field is missing",
      fr: "Le champ de langue est vide",
    },
  },
  license: {
    tab: "dataID",
    validation: (val) => val,
    error: {
      en: "Please select a license for the dataset",
      fr: "Veuillez sélectionner une licence pour le jeu de données",
    },
  },
  // if biological data then geographic description is required
  map: {
    error: (val) => {
      const problems = val ? bboxProblems(val) : [];
      if (problems.length)
        return {
          en: problems.map((p) => p.en).join("; "),
          fr: problems.map((p) => p.fr).join("; "),
        };
      if (val && val.polygon)
        return {
          en: "Polygon coordinates are invalid: use space separated lat,lon pairs that start and end with the same point",
          fr: "Les coordonnées du polygone sont invalides : utilisez des paires lat,lon séparées par des espaces qui commencent et se terminent par le même point",
        };
      return {
        en: "Spatial information is missing",
        fr: "Les informations spatiales sont manquantes",
      };
    },
    tab: "spatial",
    validation: (val, record) => {
      if (!val) return false;
      const north = parseFloat(val.north);
      const south = parseFloat(val.south);
      const east = parseFloat(val.east);
      const west = parseFloat(val.west);
      const { polygon, description } = val;

      return (
        (!Number.isNaN(north) &&
          !Number.isNaN(south) &&
          !Number.isNaN(east) &&
          !Number.isNaN(west) &&
          north >= south &&
          east >= west &&
          validateLatitude(north) &&
          validateLatitude(south) &&
          validateLongitude(east) &&
          validateLongitude(west)) ||
        (polygon && polygonIsValid(polygon)) ||
        !hasResourceType(record.resourceType) ||
        (resourceTypeIncludes(record.resourceType, "biota") && description)
      );
    },
  },

  verticalExtentMin: {
    tab: "spatial",

    validation: (val, record) => val || record.noVerticalExtent,
    error: {
      en: "Missing Vertical Extent Min",
      fr: "Étendue verticale manquante Min",
    },
  },
  verticalExtentMax: {
    tab: "spatial",
    validation: (val, record) => val || record.noVerticalExtent,
    error: {
      en: "Missing Vertical Extent Max",
      fr: "Étendue verticale manquante Max",
    },
  },
  verticalExtentDirection: {
    tab: "spatial",
    validation: (val, record) => val || record.noVerticalExtent,
    error: {
      en: "Missing Vertical Extent Direction",
      fr: "Direction de l'étendue verticale manquante",
    },
  },
  // at least one contact has to have a role and a org or individual name
  // at least one contact has to appear in citation
  contacts: {
    tab: "contacts",
    validation: (val) =>
      val &&
      // every contact must have a role and name
      val.every(contactIsFilled) &&
      val.every(
        (contact) =>
          validateEmail(contact.indEmail) &&
          validateEmail(contact.orgEmail) &&
          validateURL(contact.orgURL),
      ) &&
      val
        .filter(contactIsFilled)
        .find((contact) => contact.role.includes("custodian")) &&
      val
        .filter(contactIsFilled)
        .find((contact) => contact.role.includes("owner")) &&
      val.filter(contactIsFilled).find((contact) => contact.inCitation),
    error: {
      en: "Every contact must have at least one role checked, and 'Data Owner' or 'Metadata Custodian' must be added to at least one contact. One contact can occupy multiple roles. Email addresses must be in the form of user@example.com and URLs must be valid.  At least one contact must be selected to appear in the citation.",
      fr: "Chaque contact doit avoir au moins un rôle coché, et « Propriétaire des données » ou « Dépositaire des métadonnées » doit être ajouté à au moins un contact. Un contact peut occuper plusieurs rôles. Les adresses e-mail doivent être au format user@example.com et les URL doivent être valides. Au moins un contact doit être sélectionné pour apparaître dans la citation.",
    },
  },
  distribution: {
    tab: "resources",
    validation: (val) =>
      Array.isArray(val) &&
      val.filter((dist) => dist.name && dist.url && validator.isURL(dist.url))
        .length,

    error: {
      en: "Must have at least one resource. If a URL is included it must be valid.",
      fr: "Doit avoir au moins une ressource. Vérifiez si votre URL est valide.",
    },
  },
  associated_resources: {
    tab: "relatedworks",
    validation: (val) =>
      !val ||
      (val &&
        val.every(
          (work) =>
            work.title &&
            work.title.en &&
            work.title.fr &&
            work.authority &&
            work.code &&
            work.association_type,
        )),
    error: {
      en: "Related works must contain a Title, Identifier, Identifier Type, and a Relation Type to be valid.",
      fr: "Les ressources connexes doivent contenir un titre, un identifiant, un type d'identifiant et un type de relation pour être valides.",
    },
  },

  // if lineageStep.scope == 'collectionSession' then statment is required
  // if lineageStep.processingStep length > 0 then title and description are required
  history: {
    tab: "lineage",
    validation: (val) =>
      !val ||
      (Array.isArray(val) &&
        val.every(
          (lineageStep) =>
            !lineageStep.processingStep ||
            (lineageStep.processingStep &&
              lineageStep.processingStep.every(
                (pStep) => pStep.title && pStep.description,
              )),
        ) &&
        val.every(
          (lineageStep) =>
            !lineageStep.source ||
            (lineageStep.source &&
              lineageStep.source.every(
                (pStep) => pStep.title && pStep.description,
              )),
        ) &&
        val.every(
          (lineageStep) =>
            lineageStep.scope !== "collectionSession" ||
            (lineageStep.scope === "collectionSession" &&
              lineageStep.statement.en &&
              lineageStep.statement.fr),
        )),
    error: {
      en: "Lineage must contain a title and description for each processing step and source. If lineage scope is set to 'data collection' then lineage statement is required",
      fr: "La généalogie des données doit contenir un titre et une description pour chaque étape de traitement. Si le cadre est défini sur « collecte de données », alors une déclaration de généalogie des données est requise",
    },
  },
  platforms: {
    tab: "platform",
    validation: (val, record) =>
      record.noPlatform ||
      val.every((platform) => platform.type && platform.id) ||
      !record.metadataScope ||
      record.metadataScopeIso === "model",
    error: {
      en: "Missing platform type or ID",
      fr: "Type ou ID de plateforme manquant.",
    },
  },
  instruments: {
    tab: "platformInstruments",
    validation: (val, record) => {
      const platforms = record.platforms || [];
      return val.every(
        (instrument) =>
          instrument.id && (platforms.length < 2 || instrument.platform),
      );
    },
    error: {
      en: "Instrument ID is required. When multiple platforms are defined, each instrument must be associated to a platform.",
      fr: "L'identifiant de l'instrument est requis. Lorsque plusieurs plates-formes sont définies, chaque instrument doit être associé à une plate-forme.",
    },
  },
  taxa: {
    tab: "taxa",
    validation: (val, record) => record.noTaxa || val,
    error: {
      en: "Missing Taxonomic Coverage",
      fr: "Couverture taxonomique manquante.",
    },
  },
};

export const validateField = (record, fieldName) => {
  const valueToValidate = record[fieldName];
  // if no validator funciton exists, then it is not a required field
  const validationFunction =
    (validators[fieldName] && validators[fieldName].validation) || (() => true);

  return validationFunction && validationFunction(valueToValidate, record);
};

export const getErrorsByTab = (record) => {
  const fields = Object.keys(validators);
  const invalidFields = fields.filter((field) => !validateField(record, field));
  const fieldErrorInfo = invalidFields.map((field) => {
    const { error, tab } = validators[field];
    return {
      error: typeof error === "function" ? error(record[field], record) : error,
      tab,
    };
  });

  return fieldErrorInfo.reduce((acc, { error, tab }) => {
    if (!acc[tab]) acc[tab] = [];
    acc[tab].push(error);
    return acc;
  }, {});
};

export const warnings = {
  distribution: {
    tab: "resources",
    validation: async (val) => {
      const processedVal = await Promise.all(
        val.map(async (dist) => {
          const res = await checkURLActive(dist.url);
          return { ...dist, status: res.data };
        }),
      );
      const filterVal = processedVal.filter((dist) => !dist.status);
      return filterVal.length;
    },
    error: {
      en: "Resource URL is not accessible. This could be because it has not been created yet or is otherwise unreachable",
      fr: "L'URL de la ressource n'est pas accessible. Cela peut être dû au fait qu'il n'a pas encore été créé ou qu'il est autrement inaccessible.",
    },
  },
};

export const validateFieldWarning = async (record, fieldName) => {
  const valueToValidate = record[fieldName];
  // if no validator funciton exists, then it is not a required field
  const validationFunction =
    (warnings[fieldName] && warnings[fieldName].validation) || (() => true);

  const res = await validationFunction(valueToValidate, record);
  return validationFunction && res;
};

export const percentValid = (record) => {
  const fields = Object.keys(validators);
  const numTotalRequired = fields.filter(
    (field) => !validators[field].optional,
  ).length;

  const validFieldsRequired = fields.filter(
    (field) => !validators[field].optional && validateField(record, field),
  );
  const numValidRequired = validFieldsRequired.length;

  return numValidRequired / numTotalRequired;
};
export const recordIsValid = (record) => {
  const optionalFieldsAreValid = Object.entries(validators)
    .filter(([, validatorObject]) => validatorObject.optional)
    .every(([field]) => validateField(record, field));

  return percentValid(record) === 1 && optionalFieldsAreValid;
};
